/** Canonical optional place `details` + public tag/price helpers. Wire field names unchanged. */

export const AMENITY_OPTIONS = Object.freeze([
  { id: 'wifi', label: 'Wi‑Fi' },
  { id: 'outdoor-seating', label: 'Outdoor seating' },
  { id: 'pet-friendly', label: 'Pet friendly' },
  { id: 'card-payment', label: 'Card payment' },
  { id: 'cash-only', label: 'Cash only' },
  { id: 'reservations', label: 'Reservations' },
  { id: 'smoking-area', label: 'Smoking area' },
  { id: 'non-smoking', label: 'Non-smoking' },
  { id: 'vegan-options', label: 'Vegan options' },
  { id: 'live-music', label: 'Live music' },
  { id: 'kid-friendly', label: 'Kid friendly' },
  { id: 'wheelchair-accessible', label: 'Wheelchair accessible' },
  { id: 'parking', label: 'Parking' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'takeaway', label: 'Takeaway' },
]);

export const GOOD_FOR_OPTIONS = Object.freeze([
  { id: 'date-night', label: 'Date night' },
  { id: 'groups', label: 'Groups' },
  { id: 'work-laptop', label: 'Laptop work' },
  { id: 'late-night', label: 'Late night' },
  { id: 'brunch', label: 'Brunch' },
  { id: 'family', label: 'Family' },
  { id: 'solo', label: 'Solo' },
  { id: 'tourists', label: 'Tourists' },
  { id: 'locals', label: 'Locals' },
]);

export const PRICE_RANGE_OPTIONS = Object.freeze([
  { value: 1, label: 'Cheap eats' },
  { value: 2, label: 'Mid-range' },
  { value: 3, label: 'Upscale' },
  { value: 4, label: 'Splurge' },
]);

export const AMENITY_IDS = Object.freeze(AMENITY_OPTIONS.map((opt) => opt.id));
export const GOOD_FOR_IDS = Object.freeze(GOOD_FOR_OPTIONS.map((opt) => opt.id));

const AMENITY_ID_SET = new Set(AMENITY_IDS);
const GOOD_FOR_ID_SET = new Set(GOOD_FOR_IDS);
const HIDDEN_PUBLIC_TAGS = new Set(['archived', 'secret', 'partner', 'sarajevo', 'stage']);
const TABLE_RESERVE_CATEGORIES = new Set(['food', 'coffee', 'drinks', 'bar', 'bars']);

export function isHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

export function amenityLabel(id) {
  return AMENITY_OPTIONS.find((opt) => opt.id === id)?.label || '';
}

export function goodForLabel(id) {
  return GOOD_FOR_OPTIONS.find((opt) => opt.id === id)?.label || '';
}

export function filterAllowedIds(value, allowlist) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  value.forEach((item) => {
    const id = typeof item === 'string' ? item.trim() : '';
    if (!id || !allowlist.has(id) || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

export function filterAmenityIds(value) {
  return filterAllowedIds(value, AMENITY_ID_SET);
}

export function filterGoodForIds(value) {
  return filterAllowedIds(value, GOOD_FOR_ID_SET);
}

/**
 * @param {unknown} n
 * @returns {{ value: number, filled: string, muted: string, label: string }|null}
 */
export function formatPriceRange(n) {
  const value = Number.parseInt(n, 10);
  if (!Number.isFinite(value) || value < 1 || value > 4) return null;
  const option = PRICE_RANGE_OPTIONS[value - 1];
  if (!option) return null;
  return {
    value,
    filled: '$'.repeat(value),
    muted: '$'.repeat(4 - value),
    label: option.label,
  };
}

export function placeReserveCtaLabel(mainCategory) {
  const key = String(mainCategory || '').trim().toLowerCase();
  return TABLE_RESERVE_CATEGORIES.has(key) ? 'Reserve a table' : 'Book';
}

/**
 * @param {object|null|undefined} place
 * @param {{ max?: number, from?: 'tags'|'categories'|'auto' }} [opts]
 * @returns {string[]}
 */
export function publicPlaceTags(place, { max = 8, from = 'auto' } = {}) {
  const main = String(place?.mainCategory || '').trim().toLowerCase();
  let source;
  if (from === 'tags') source = place?.tags;
  else if (from === 'categories') source = place?.categories;
  else source = Array.isArray(place?.categories) && place.categories.length
    ? place.categories
    : place?.tags;

  if (!Array.isArray(source)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of source) {
    const tag = String(raw || '').trim();
    if (!tag) continue;
    const key = tag.toLowerCase();
    if (HIDDEN_PUBLIC_TAGS.has(key) || (main && key === main) || seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
    if (out.length >= max) break;
  }
  return out;
}
